import { isEmpty } from "lodash";
import { Merchant } from "@/dto/merchant";
import { CloverColor, DudaWebsiteData } from "./cloverThemeMapper";
import { getContrastTextColor } from "./colorUtils";
import { redirect } from "next/navigation";

export const isDudaWebsite = (merchant: Merchant): boolean => {
  if (!merchant) return false;
  if (!merchant.isRetail) return false;

  const dudaEnabledFlagValue = merchant.launchDarklyFeatures?.some(
    (feature: any) => feature.name === "DUDA_WEBSITE_BUILDER" && feature.enabled
  );
  if (!dudaEnabledFlagValue) return false;
  if (isEmpty(merchant.dudaWebsiteData)) return false;

  const dudaWebsiteData = merchant.dudaWebsiteData;

  if (!dudaWebsiteData.siteId || !dudaWebsiteData.publishedUrl) return false;

  return true;
};

const resolveColorFromTheme = (
  color: string,
  themeColors: CloverColor[]
): string => {
  if (!color || !themeColors) return color;

  if (color.startsWith("var(--")) {
    const colorId = color.replace("var(--", "").replace(")", "");
    const themeColor = themeColors.find((c) => c.id === colorId);
    return themeColor?.value || color;
  }

  return color;
};

const isSafeDudaNavigationHref = (href: string): boolean => {
  // Require a string starting with a single /, excluding absolute URLs and
  // executable schemes. Reject // host URLs, backslashes, and control characters
  // (such as tabs and newlines) that can alter how browsers interpret the URL.
  if (
    typeof href !== "string" ||
    !href.startsWith("/") ||
    href.startsWith("//") ||
    /[\\\u0000-\u001f\u007f]/.test(href)
  ) {
    return false;
  }

  // Use a reserved .invalid placeholder to resolve relative paths and check
  // that the origin stays unchanged. URL parsing makes no network request.
  const validationOrigin = "https://duda-navigation.invalid";
  try {
    return new URL(href, validationOrigin).origin === validationOrigin;
  } catch {
    return false;
  }
};

const getDudaNavigationLinks = (
  navigation: DudaWebsiteData["navigation"]
): { href: string; text: string; show: boolean }[] => {
  if (
    !navigation ||
    navigation.length === 0 ||
    !navigation[0]?.navigation
  )
    // As of now returning only Shop link. Once support started for all verticals, based on business type it has to return the links
    return [
      { href: "/", text: "Home", show: true },
      { href: "/shop", text: "Shop", show: true },
    ];

  return navigation[0].navigation
    .map((link) => ({
      href: link?.url || "/",
      text: link?.title ?? "",
      show: true,
    }))
    .filter((link) => isSafeDudaNavigationHref(link.href));
};

export const processDudaWebsiteData = (
  dudaWebsiteData: DudaWebsiteData | null | undefined
): DudaWebsiteData | null => {
  if (!dudaWebsiteData || isEmpty(dudaWebsiteData)) return null;

  const headerBgColor = resolveColorFromTheme(
    dudaWebsiteData.headerStyles?.bgColor || "",
    dudaWebsiteData.theme?.colors
  );

  const footerBgColor = resolveColorFromTheme(
    dudaWebsiteData.footerStyles?.bgColor || "",
    dudaWebsiteData.theme?.colors
  );

  const updated = {
    ...dudaWebsiteData,
    headerStyles: dudaWebsiteData.headerStyles
      ? {
          bgColor: headerBgColor,
          color: getContrastTextColor(headerBgColor),
        }
      : undefined,
    footerStyles: {
      bgColor: footerBgColor,
      color: getContrastTextColor(footerBgColor),
    },
    navigationLinks: getDudaNavigationLinks(dudaWebsiteData.navigation),
  };

  return updated;
};

export const handleUnPublishedDudaWebsite = (merchant: Merchant) => {
  const isDuda = isDudaWebsite(merchant);
  if (!isDuda) return false;

  if (merchant.dudaWebsiteData?.status === "UNPUBLISHED") {
    redirect("/not-published");
  }
};
